
import { Vue, Component } from 'vue-property-decorator';
import { showErrorAlertDialog } from '@/features/common/helpers';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import {
  FedExValidateAddressRequest,
  FedExSecureCodeOption,
  FedExVerificationMethod,
} from '../types/fedexRegistration';
import {
  validateFedExAddress,
  generateFedExPin,
  validateFedExPin,
  validateFedExInvoice,
} from '../services/fedexRegistration.services';
import FedExEulaStep from '../components/fedex/FedExEulaStep.vue';
import FedExAddressStep from '../components/fedex/FedExAddressStep.vue';
import FedExVerificationMethodStep from '../components/fedex/FedExVerificationMethodStep.vue';
import FedExMfaOptionStep from '../components/fedex/FedExMfaOptionStep.vue';
import FedExInvoiceStep from '../components/fedex/FedExInvoiceStep.vue';
import FedExPinStep from '../components/fedex/FedExPinStep.vue';
import FedExSuccessStep from '../components/fedex/FedExSuccessStep.vue';

type FedExStep = 'eula' | 'address' | 'methodSelection' | 'mfaOption' | 'pin' | 'invoice' | 'success';

@Component({
  components: {
    FedExEulaStep,
    FedExAddressStep,
    FedExVerificationMethodStep,
    FedExMfaOptionStep,
    FedExInvoiceStep,
    FedExPinStep,
    FedExSuccessStep,
  },
})
export default class FedExRegistration extends Vue {
  private currentStep: FedExStep = 'eula';

  private isLoading = false;

  private isResending = false;

  private resendSuccess = false;

  private accountAuthToken = '';

  private accountNumber = '';

  private payorCountryCode = '';

  private selectedMfaOption: FedExSecureCodeOption | null = null;

  private selectedVerificationMethod: FedExVerificationMethod | null = null;

  private hasInvoiceOption = false;

  private addressFormData: FedExValidateAddressRequest | null = null;

  private mfaData = {
    secureCodeOptions: [] as FedExSecureCodeOption[],
    maskedEmail: '',
    maskedPhoneNumber: '',
  };

  private static readonly STEP_INDEX_MAP: Record<FedExStep, number> = {
    eula: 1,
    address: 2,
    methodSelection: 3,
    mfaOption: 3,
    invoice: 3,
    pin: 3,
    success: 3,
  };

  get currentStepIndex(): number {
    return FedExRegistration.STEP_INDEX_MAP[this.currentStep] || 1;
  }

  get steps(): string[] {
    return [
      this.$t('carrierAccounts.fedexRegistration.steps.eula') as string,
      this.$t('carrierAccounts.fedexRegistration.steps.address') as string,
      this.$t('carrierAccounts.fedexRegistration.steps.pin') as string,
    ];
  }

  get fedexFallbackMessage(): string {
    return this.$t('carrierAccounts.errorMessages.FedExRegistrationException') as string;
  }

  private async onAddressSubmit(formData: FedExValidateAddressRequest): Promise<void> {
    this.isLoading = true;

    this.addressFormData = formData;
    this.accountNumber = formData.accountNumber;
    this.payorCountryCode = formData.payorCountryCode;

    const response = await validateFedExAddress(formData);

    this.isLoading = false;

    if (!response.successful || !response.data) {
      showErrorAlertDialog(response.errorMessage);
      return;
    }

    const { data } = response;

    if (!data.mfaRequired && data.carrierAccounts) {
      this.onRegistrationComplete();
      return;
    }

    if (!data.accountAuthToken) {
      showErrorAlertDialog(this.fedexFallbackMessage);
      return;
    }

    this.accountAuthToken = data.accountAuthToken;
    this.hasInvoiceOption = data.hasInvoiceOption || false;
    this.mfaData = {
      secureCodeOptions: data.secureCodeOptions || [],
      maskedEmail: data.maskedEmail || '',
      maskedPhoneNumber: data.maskedPhoneNumber || '',
    };

    if (this.mfaData.secureCodeOptions.length) {
      this.currentStep = 'mfaOption';
      return;
    }

    showErrorAlertDialog(this.fedexFallbackMessage);
  }

  private onVerificationMethodSubmit(method: FedExVerificationMethod): void {
    this.selectedVerificationMethod = method;

    if (method === 'INVOICE') {
      this.currentStep = 'invoice';
    } else {
      this.currentStep = 'mfaOption';
    }
  }

  private async onMfaOptionSubmit(option: FedExSecureCodeOption): Promise<void> {
    this.isLoading = true;
    this.selectedMfaOption = option;

    const response = await generateFedExPin({
      accountAuthToken: this.accountAuthToken,
      option,
    });

    this.isLoading = false;

    if (!response.successful) {
      showErrorAlertDialog(response.errorMessage);
      return;
    }

    this.currentStep = 'pin';
  }

  private onMfaOptionBack(): void {
    this.currentStep = 'address';
  }

  private async onInvoiceSubmit(invoiceData: {
    invoiceNumber: string;
    invoiceDate: string;
    invoiceAmount: string;
    currency: string;
  }): Promise<void> {
    this.isLoading = true;

    const response = await validateFedExInvoice({
      accountAuthToken: this.accountAuthToken,
      accountNumber: this.accountNumber,
      payorCountryCode: this.payorCountryCode,
      invoiceNumber: invoiceData.invoiceNumber,
      invoiceCurrency: invoiceData.currency,
      invoiceDate: invoiceData.invoiceDate,
      invoiceAmount: parseFloat(invoiceData.invoiceAmount),
    });

    this.isLoading = false;

    if (!response.successful || !response.data) {
      showErrorAlertDialog(response.errorMessage);
      return;
    }

    this.onRegistrationComplete();
  }

  private async onPinSubmit(pin: string): Promise<void> {
    this.isLoading = true;

    const response = await validateFedExPin({
      accountAuthToken: this.accountAuthToken,
      secureCodePin: pin,
      accountNumber: this.accountNumber,
      payorCountryCode: this.payorCountryCode,
    });

    this.isLoading = false;

    if (!response.successful || !response.data) {
      showErrorAlertDialog(response.errorMessage);
      return;
    }

    this.onRegistrationComplete();
  }

  private async onResendPin(): Promise<void> {
    if (!this.selectedMfaOption) return;

    this.isResending = true;
    this.resendSuccess = false;

    const response = await generateFedExPin({
      accountAuthToken: this.accountAuthToken,
      option: this.selectedMfaOption,
    });

    this.isResending = false;

    if (!response.successful) {
      showErrorAlertDialog(response.errorMessage);
      return;
    }

    this.resendSuccess = true;
  }

  private onRegistrationComplete(): void {
    eventBus.$emit(eventNames.FETCH_CARRIER_ACCOUNTS);
    this.currentStep = 'success';
  }

  private navigateBack(): void {
    this.$router.push({ name: 'carrier-accounts' });
  }
}
